import {
  DEFAULT_SLOT_MINUTES,
  DEFAULT_START_HOUR,
  DEFAULT_END_HOUR,
  parseLocalDate,
  formatDateString,
  readCssLayout,
} from "./utils";
import { DayView } from "./views/day_view";
import { WeekView } from "./views/week_view";
import { MonthView } from "./views/month_view";

const VIEW_TYPES = ["day", "week", "month"];

// CalendarScheduler — framework-agnostic calendar component.
//
// Public API:
//   new CalendarScheduler({ element, date, view, staff, events, ...options })
//   calendar.setData({ staff, events })
//   calendar.setView("day" | "week" | "month")
//   calendar.setDate("YYYY-MM-DD")
//   calendar.refresh()                     -- re-render in place
//   calendar.destroy()                     -- tear down DOM and listeners
//   calendar.on(name, handler)             -- subscribe to "calendar:<name>" events
//   calendar.off(name, handler)
//
// Events dispatched (all bubble from `element`):
//   calendar:view-change      { oldView, newView }
//   calendar:date-change      { oldDate, newDate, view }
//   calendar:slot-click       { staffId, startTime, endTime }   (Day/Week)
//   calendar:date-cell-click  { date }                          (Month — drill-into-day signal)
//   calendar:event-click      { kind, id, appointmentId|blockId }
//   calendar:event-move       { id, kind, originalStart, newStart, originalEnd, newEnd, originalStaffId, newStaffId }
//   calendar:event-resize     { id, kind, originalEnd, newEnd }
//
// The component is "dumb": it renders, dispatches events, and never mutates remote state.
// Consumers wire up persistence by listening for these events from outside.
export class CalendarScheduler {
  /**
   * @param {object} options
   * @param {HTMLElement} options.element - Mount point. Required.
   * @param {string} options.date         - YYYY-MM-DD. Defaults to today.
   * @param {string} [options.view]       - "day" | "week" | "month". Default "day".
   * @param {Array}  [options.staff]      - [{ id, name, avatarUrl }]. Default [].
   * @param {Array}  [options.events]     - calendar items. Default [].
   * @param {number} [options.startHour]  - First visible hour (0-23). Default 9.
   * @param {number} [options.endHour]    - Last visible hour (0-23). Default 18.
   * @param {number} [options.slotMinutes] - Granularity of one row. Default 30.
   * @param {string[]} [options.availableViews] - Subset of VIEW_TYPES. Default all 3.
   */
  constructor(options = {}) {
    if (!options.element || !(options.element instanceof HTMLElement)) {
      throw new Error("CalendarScheduler: `element` option is required");
    }

    this.element = options.element;
    this.date = options.date || formatDateString(new Date());
    this.view = VIEW_TYPES.includes(options.view) ? options.view : "day";
    this.staff = options.staff || [];
    this.events = options.events || [];
    this.startHour = options.startHour ?? DEFAULT_START_HOUR;
    this.endHour = options.endHour ?? DEFAULT_END_HOUR;
    this.availableViews = (options.availableViews || VIEW_TYPES).filter((v) =>
      VIEW_TYPES.includes(v)
    );
    if (this.availableViews.length === 0) this.availableViews = ["day"];
    if (!this.availableViews.includes(this.view)) {
      this.view = this.availableViews[0];
    }

    this._slotMinutes = options.slotMinutes ?? DEFAULT_SLOT_MINUTES;
    this._activeView = null;

    // Mark element so the CSS variables and bubbling-event selector lookups work.
    this.element.classList.add("calendar-scheduler-root");
    this.element.dataset.calendarRoot = "true";

    // Compact-mode (mobile) detection. Re-render when the breakpoint flips so
    // views can swap between desktop multi-column and mobile single-column layouts.
    this._compactMql = window.matchMedia("(max-width: 1023px)");
    this._onCompactChange = () => this._build();
    if (this._compactMql.addEventListener) {
      this._compactMql.addEventListener("change", this._onCompactChange);
    } else {
      // Safari < 14 fallback.
      this._compactMql.addListener(this._onCompactChange);
    }

    this._build();
  }

  // True when the viewport is below the lg breakpoint. Views read this to decide
  // between desktop and mobile layouts.
  get compact() {
    return this._compactMql.matches;
  }

  // ---- public API ----

  setData({ staff, events } = {}) {
    if (Array.isArray(staff)) this.staff = staff;
    if (Array.isArray(events)) this.events = events;
    this._activeView?.setData();
  }

  setView(view) {
    if (!this.availableViews.includes(view)) return;
    if (this.view === view) return;
    const oldView = this.view;
    this.view = view;
    this._emit("view-change", { oldView, newView: view });
    this._build();
  }

  setDate(newDate) {
    if (!newDate || newDate === this.date) return;
    const oldDate = this.date;
    this.date = newDate;
    this._emit("date-change", { oldDate, newDate, view: this.view });
    this._build();
  }

  shiftDate(direction) {
    const current = parseLocalDate(this.date);
    if (this.view === "week") current.setDate(current.getDate() + direction * 7);
    else if (this.view === "month") current.setMonth(current.getMonth() + direction);
    else current.setDate(current.getDate() + direction);
    this.setDate(formatDateString(current));
  }

  goToToday() {
    this.setDate(formatDateString(new Date()));
  }

  refresh() {
    this._build();
  }

  destroy() {
    this._activeView?.destroy();
    this._activeView = null;
    if (this._compactMql && this._onCompactChange) {
      if (this._compactMql.removeEventListener) {
        this._compactMql.removeEventListener("change", this._onCompactChange);
      } else {
        this._compactMql.removeListener(this._onCompactChange);
      }
    }
    this.element.replaceChildren();
    this.element.classList.remove("calendar-scheduler-root");
    delete this.element.dataset.calendarRoot;
  }

  on(name, handler) {
    this.element.addEventListener(`calendar:${name}`, handler);
  }

  off(name, handler) {
    this.element.removeEventListener(`calendar:${name}`, handler);
  }

  // Layout values resolved from CSS custom properties on the root.
  // Read once per build; consumers can call refresh() if they change CSS at runtime.
  get layout() {
    return {
      ...readCssLayout(this.element),
      slotMinutes: this._slotMinutes,
    };
  }

  // ---- internal ----

  _build() {
    this._activeView?.destroy();
    this._activeView = null;
    this.element.replaceChildren();

    if (this.view === "day") {
      this._activeView = new DayView(this);
    } else if (this.view === "week") {
      this._activeView = new WeekView(this);
    } else if (this.view === "month") {
      this._activeView = new MonthView(this);
    }

    this._activeView?.mount(this.element);
  }

  _emit(name, detail) {
    this.element.dispatchEvent(
      new CustomEvent(`calendar:${name}`, { detail, bubbles: true })
    );
  }
}
